import React, { useState, useEffect, useRef } from 'react';

const FileDropzone = ({ files, setFiles, accept = 'image/*,.pdf' }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [previewUrls, setPreviewUrls] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    // Revoke previous URLs
    previewUrls.forEach((u) => URL.revokeObjectURL(u));
    if (files && files.length > 0) {
      const urls = files.map((f) => URL.createObjectURL(f));
      setPreviewUrls(urls);
      return () => urls.forEach((u) => URL.revokeObjectURL(u));
    } else {
      setPreviewUrls([]);
    }
  }, [files]);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const dt = e.dataTransfer;
    if (dt && dt.files && dt.files.length > 0) {
      const selected = Array.from(dt.files || []);
      if (selected.length > 0) {
        setFiles([...(files || []), ...selected]);
      }
    }
  };

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length > 0) {
      setFiles([...(files || []), ...selected]);
    }
  };

  const removeAt = (idx) => {
    const next = [...(files || [])];
    next.splice(idx, 1);
    setFiles(next);
  };

  const clearAll = () => {
    setFiles([]);
  };

  const isPdfFile = (file, url) => {
    const name = String(file?.name || '').toLowerCase();
    return (file?.type === 'application/pdf') || name.endsWith('.pdf') || /\.pdf($|\?)/i.test(String(url || ''));
  };

  return (
    <div
      onClick={() => fileInputRef.current && fileInputRef.current.click()}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{
        border: '1px dashed #d1d5db',
        borderRadius: 8,
        padding: 16,
        textAlign: 'center',
        background: isDragging ? 'rgba(37, 99, 235, 0.06)' : 'rgba(0,0,0,0.02)',
        cursor: 'pointer',
      }}
    >
      {previewUrls.length === 0 ? (
        <div style={{ color: '#6b7280' }}>
          Drag & drop files here, or click to select
        </div>
      ) : (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
            {previewUrls.map((url, idx) => (
              <div key={idx} style={{ position: 'relative', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
                {/* Show image preview when possible; otherwise a generic file badge */}
                {files?.[idx]?.type?.startsWith('image/') ? (
                  <img
                    src={url}
                    alt={`File preview ${idx + 1}`}
                    style={{ width: '100%', height: 100, objectFit: 'cover' }}
                  />
                ) : isPdfFile(files?.[idx], url) ? (
                  <iframe
                    src={url}
                    title={files?.[idx]?.name || `PDF preview ${idx + 1}`}
                    style={{
                      width: '100%',
                      height: 100,
                      border: 0,
                      background: '#fff',
                    }}
                  />
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: 100, background: '#f9fafb' }}>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>{files?.[idx]?.name || 'File'}</span>
                  </div>
                )}
                <button
                  className="btn"
                  style={{ position: 'absolute', top: 4, right: 4, padding: '4px 8px', fontSize: 12 }}
                  onClick={(e) => { e.stopPropagation(); removeAt(idx); }}
                >Remove</button>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
            <button className="btn" onClick={(e) => { e.stopPropagation(); fileInputRef.current && fileInputRef.current.click(); }}>Add more</button>
            <button className="btn" onClick={(e) => { e.stopPropagation(); clearAll(); }}>Clear all</button>
          </div>
        </div>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />
    </div>
  );
};

export default FileDropzone;
